/**
 * Field renderer components for the dynamic form renderer
 * Each function renders a specific field type integrated with react-hook-form
 */

import type { ControllerRenderProps } from "react-hook-form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Calendar, Upload, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useRef } from "react";
import type { FormField } from "@/types/agent-ui-types";

type RHFField = ControllerRenderProps<Record<string, unknown>, string>;

interface FieldRendererProps {
  field: FormField;
  rhfField: RHFField;
}

/** Renders a text input */
export function TextFieldRenderer({ field, rhfField }: FieldRendererProps) {
  return (
    <Input
      placeholder={field.placeholder}
      {...rhfField}
      value={(rhfField.value as string) ?? ""}
    />
  );
}

/** Renders an email input */
export function EmailFieldRenderer({ field, rhfField }: FieldRendererProps) {
  return (
    <Input
      type="email"
      placeholder={field.placeholder}
      {...rhfField}
      value={(rhfField.value as string) ?? ""}
    />
  );
}

/** Renders a number input with optional prefix/suffix */
export function NumberFieldRenderer({ field, rhfField }: FieldRendererProps) {
  return (
    <div className="flex items-center gap-1">
      {field.config?.prefix && (
        <span className="text-sm text-muted-foreground shrink-0">
          {field.config.prefix}
        </span>
      )}
      <Input
        type="number"
        placeholder={field.placeholder}
        step={field.config?.step}
        {...rhfField}
        value={(rhfField.value as string) ?? ""}
        onChange={(e) => rhfField.onChange(e.target.value)}
      />
      {field.config?.suffix && (
        <span className="text-sm text-muted-foreground shrink-0">
          {field.config.suffix}
        </span>
      )}
    </div>
  );
}

/** Renders a textarea */
export function TextareaFieldRenderer({ field, rhfField }: FieldRendererProps) {
  return (
    <Textarea
      placeholder={field.placeholder}
      rows={field.config?.rows ?? 3}
      {...rhfField}
      value={(rhfField.value as string) ?? ""}
    />
  );
}

/** Renders a select dropdown */
export function SelectFieldRenderer({ field, rhfField }: FieldRendererProps) {
  return (
    <Select
      value={(rhfField.value as string) ?? ""}
      onValueChange={rhfField.onChange}
    >
      <SelectTrigger className="w-full">
        <SelectValue placeholder={field.placeholder ?? "Select..."} />
      </SelectTrigger>
      <SelectContent>
        {field.options
          ?.filter((opt) => opt.value !== '')
          .map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
      </SelectContent>
    </Select>
  );
}

/** Renders radio buttons */
export function RadioFieldRenderer({ field, rhfField }: FieldRendererProps) {
  const layout = field.config?.layout ?? "vertical";

  return (
    <RadioGroup
      value={(rhfField.value as string) ?? ""}
      onValueChange={rhfField.onChange}
      className={layout === "horizontal" ? "flex gap-4" : "gap-2"}
    >
      {field.options?.map((opt) => (
        <div key={opt.value} className="flex items-center gap-2">
          <RadioGroupItem
            value={opt.value}
            id={`${field.id}-${opt.value}`}
          />
          <Label
            htmlFor={`${field.id}-${opt.value}`}
            className="text-sm font-normal cursor-pointer"
          >
            {opt.label}
          </Label>
        </div>
      ))}
    </RadioGroup>
  );
}

/** Renders checkboxes for multi-select */
export function CheckboxFieldRenderer({ field, rhfField }: FieldRendererProps) {
  const values = (rhfField.value as string[]) ?? [];
  const layout = field.config?.layout ?? "vertical";

  const toggle = (optValue: string) => {
    const next = values.includes(optValue)
      ? values.filter((v) => v !== optValue)
      : [...values, optValue];
    rhfField.onChange(next);
  };

  return (
    <div className={layout === "horizontal" ? "flex gap-4" : "space-y-2"}>
      {field.options?.map((opt) => (
        <div key={opt.value} className="flex items-center gap-2">
          <Checkbox
            id={`${field.id}-${opt.value}`}
            checked={values.includes(opt.value)}
            onCheckedChange={() => toggle(opt.value)}
          />
          <Label
            htmlFor={`${field.id}-${opt.value}`}
            className="text-sm font-normal cursor-pointer"
          >
            {opt.label}
          </Label>
        </div>
      ))}
    </div>
  );
}

/** Renders a toggle switch */
export function ToggleFieldRenderer({ field, rhfField }: FieldRendererProps) {
  return (
    <div className="flex items-center gap-3">
      <Switch
        checked={(rhfField.value as boolean) ?? false}
        onCheckedChange={rhfField.onChange}
      />
      {field.placeholder && (
        <span className="text-sm text-muted-foreground">
          {field.placeholder}
        </span>
      )}
    </div>
  );
}

/** Renders a single file upload */
export function FileFieldRenderer({ field, rhfField }: FieldRendererProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const file = rhfField.value as File | undefined;

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept={field.config?.accept}
        onChange={(e) => {
          const selected = e.target.files?.[0];
          if (selected) {
            if (field.config?.maxSize && selected.size > field.config.maxSize) {
              return;
            }
            rhfField.onChange(selected);
          }
        }}
      />
      {file ? (
        <div className="flex items-center gap-2 rounded-lg border p-3">
          <span className="text-sm truncate flex-1">{file.name}</span>
          <span className="text-xs text-muted-foreground shrink-0">
            {(file.size / 1024).toFixed(1)} KB
          </span>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6 shrink-0"
            onClick={() => rhfField.onChange(undefined)}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      ) : (
        <div
          className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 transition-colors"
          onClick={() => inputRef.current?.click()}
        >
          <Upload className="h-5 w-5 mx-auto text-muted-foreground mb-1.5" />
          <p className="text-sm text-muted-foreground">
            {field.config?.dropzoneText ?? "Click to upload a file"}
          </p>
          {field.config?.accept && (
            <p className="text-xs text-muted-foreground mt-1">
              Accepts: {field.config.accept}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/** Renders a multi-file upload */
export function FilesFieldRenderer({ field, rhfField }: FieldRendererProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const files = (rhfField.value as File[]) ?? [];
  const maxFiles = field.config?.maxFiles ?? 10;

  const addFiles = (fileList: FileList) => {
    const newFiles = Array.from(fileList).filter((f) => {
      if (field.config?.maxSize && f.size > field.config.maxSize) return false;
      return true;
    });
    const combined = [...files, ...newFiles].slice(0, maxFiles);
    rhfField.onChange(combined);
  };

  const removeFile = (index: number) => {
    rhfField.onChange(files.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-2">
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        multiple
        accept={field.config?.accept}
        onChange={(e) => {
          if (e.target.files) addFiles(e.target.files);
          e.target.value = "";
        }}
      />

      {files.length > 0 && (
        <div className="space-y-1">
          {files.map((file, i) => (
            <div key={i} className="flex items-center gap-2 rounded border p-2">
              <span className="text-sm truncate flex-1">{file.name}</span>
              <span className="text-xs text-muted-foreground shrink-0">
                {(file.size / 1024).toFixed(1)} KB
              </span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-6 w-6 shrink-0"
                onClick={() => removeFile(i)}
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {files.length < maxFiles && (
        <div
          className="border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:border-primary/50 transition-colors"
          onClick={() => inputRef.current?.click()}
        >
          <Upload className="h-5 w-5 mx-auto text-muted-foreground mb-1" />
          <p className="text-xs text-muted-foreground">
            {field.config?.dropzoneText ?? "Click to upload files"}
          </p>
          <p className="text-[10px] text-muted-foreground mt-0.5">
            {files.length}/{maxFiles} files
          </p>
        </div>
      )}
    </div>
  );
}

/** Renders a date input */
export function DateFieldRenderer({ field, rhfField }: FieldRendererProps) {
  return (
    <div className="relative">
      <Input
        type="date"
        placeholder={field.placeholder}
        min={field.config?.minDate}
        max={field.config?.maxDate}
        {...rhfField}
        value={(rhfField.value as string) ?? ""}
      />
      <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
    </div>
  );
}
